import { useEffect, useState } from 'react'
import { allLocalAssets } from '../assets/localAssets'

/*
  Object URLs for every photo this browser holds, by reference.

  The renderer resolves a reference to a URL synchronously, in the middle of a
  render, and reading IndexedDB is not synchronous. So the whole set is read
  once and turned into object URLs the resolver can look up. There are at most a
  few dozen photos in a document, and a revoked URL would blank a picture that
  is still on screen, so they live as long as the page does.
*/
let cache: Record<string, string> = {}
const listeners = new Set<(next: Record<string, string>) => void>()

export function localAssetUrl(ref: string): string | undefined {
  return cache[ref]
}

async function load() {
  const assets = await allLocalAssets()
  const next: Record<string, string> = { ...cache }
  assets.forEach(asset => {
    const ref = `local:${asset.id}`
    if (!next[ref]) next[ref] = URL.createObjectURL(asset.blob)
  })
  cache = next
  listeners.forEach(listener => listener(cache))
}

/** Call after storing a photo, so the canvas can draw it at once. */
export function rememberLocalAsset(ref: string, blob: Blob) {
  if (cache[ref]) return
  cache = { ...cache, [ref]: URL.createObjectURL(blob) }
  listeners.forEach(listener => listener(cache))
}

/**
 * Hydrates the browser's photos and re-renders when one arrives.
 *
 * Mounted once by the builder page. Without it a reload shows every locally
 * held photo as the grey placeholder until something else happens to re-render.
 */
export function useLocalAssetUrls(): Record<string, string> {
  const [urls, setUrls] = useState(cache)

  useEffect(() => {
    listeners.add(setUrls)
    load().catch(() => {
      // A browser with IndexedDB switched off still edits text; the photos are
      // simply the placeholder, which is what a missing photo already looks like.
    })
    return () => {
      listeners.delete(setUrls)
    }
  }, [])

  return urls
}
